import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api, formatError } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid,
} from "recharts";
import { toast } from "sonner";
import {
  TrendingUp, Users, MessageSquare, DollarSign, Plus, Trash2, Save, LayoutDashboard,
  Image as ImageIcon, Crown, Home as HomeIcon, Folder, Gavel, CreditCard, Check, AlertCircle,
} from "lucide-react";

export default function Admin() {
  const { user, loading } = useAuth();

  if (loading) return <div className="p-8" data-testid="admin-loading">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  return (
    <div className="pastel-grain min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-6">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-500">Control Center</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight">Admin Panel</h1>
        </div>

        <Tabs defaultValue="analytics" className="w-full">
          <TabsList className="brutal-border brutal-shadow-sm rounded-xl bg-white p-1 flex flex-wrap h-auto gap-1"
            data-testid="admin-tabs">
            <TabTrigger value="analytics" icon={<LayoutDashboard size={14} />}>Analytics</TabTrigger>
            <TabTrigger value="moderation" icon={<Gavel size={14} />}>Moderators</TabTrigger>
            <TabTrigger value="categories" icon={<Folder size={14} />}>Categories</TabTrigger>
            <TabTrigger value="banners" icon={<ImageIcon size={14} />}>Banners</TabTrigger>
            <TabTrigger value="memberships" icon={<Crown size={14} />}>Memberships</TabTrigger>
            <TabTrigger value="payments" icon={<CreditCard size={14} />}>Payments</TabTrigger>
            <TabTrigger value="homepage" icon={<HomeIcon size={14} />}>Homepage</TabTrigger>
          </TabsList>

          <TabsContent value="analytics" className="mt-6"><Analytics /></TabsContent>
          <TabsContent value="moderation" className="mt-6"><Moderation /></TabsContent>
          <TabsContent value="categories" className="mt-6"><Categories /></TabsContent>
          <TabsContent value="banners" className="mt-6"><Banners /></TabsContent>
          <TabsContent value="memberships" className="mt-6"><Memberships /></TabsContent>
          <TabsContent value="payments" className="mt-6"><Payments /></TabsContent>
          <TabsContent value="homepage" className="mt-6"><Homepage /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function TabTrigger({ value, icon, children }) {
  return (
    <TabsTrigger value={value} data-testid={`tab-${value}`}
      className="rounded-lg font-bold gap-2 data-[state=active]:bg-[var(--accent)] data-[state=active]:shadow-[2px_2px_0_0_var(--ink)] data-[state=active]:border-2 data-[state=active]:border-[var(--ink)] px-3 h-9">
      {icon} {children}
    </TabsTrigger>
  );
}

function Analytics() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then((r) => setS(r.data)); }, []);
  if (!s) return <div data-testid="analytics-loading">Loading...</div>;

  const stats = [
    { label: "Users", value: s.total_users, icon: <Users size={18} />, color: "var(--primary)", delta: `+${s.new_users_7d} this week` },
    { label: "Threads", value: s.total_threads, icon: <MessageSquare size={18} />, color: "var(--accent)", delta: `+${s.new_threads_7d} this week` },
    { label: "Posts", value: s.total_posts, icon: <TrendingUp size={18} />, color: "var(--secondary)", delta: "" },
    { label: "Revenue", value: `$${s.revenue.toFixed(2)}`, icon: <DollarSign size={18} />, color: "var(--green)", delta: `${s.paid_users} paying` },
  ];

  return (
    <div className="space-y-6">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((st) => (
          <div key={st.label} data-testid={`stat-${st.label.toLowerCase()}`}
            className="brutal-border brutal-shadow-sm rounded-xl p-5" style={{ background: st.color }}>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 grid place-items-center brutal-border rounded-lg bg-white">{st.icon}</div>
              <div className="text-xs uppercase font-bold tracking-widest text-zinc-700">{st.label}</div>
            </div>
            <div className="font-display font-black text-3xl mt-3">{st.value}</div>
            {st.delta && <div className="text-xs text-zinc-700 font-medium mt-1">{st.delta}</div>}
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="brutal-border brutal-shadow-sm rounded-xl p-5 bg-white">
          <h3 className="font-display font-bold text-lg mb-4">Activity (last 7 days)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={s.daily_activity}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
              <XAxis dataKey="day" tick={{ fontWeight: 700, fontSize: 12 }} />
              <YAxis tick={{ fontWeight: 700, fontSize: 12 }} />
              <Tooltip contentStyle={{ border: "2px solid #121212", borderRadius: 8, fontWeight: 600 }} />
              <Line type="monotone" dataKey="threads" stroke="#B5E2FA" strokeWidth={3} />
              <Line type="monotone" dataKey="posts" stroke="#FFD8BE" strokeWidth={3} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="brutal-border brutal-shadow-sm rounded-xl p-5 bg-white">
          <h3 className="font-display font-bold text-lg mb-4">Top categories</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={s.top_categories}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
              <XAxis dataKey="name" tick={{ fontWeight: 700, fontSize: 11 }} />
              <YAxis tick={{ fontWeight: 700, fontSize: 12 }} />
              <Tooltip contentStyle={{ border: "2px solid #121212", borderRadius: 8, fontWeight: 600 }} />
              <Bar dataKey="threads" fill="#E0D4FF" stroke="#121212" strokeWidth={2} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function Moderation() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [nu, setNu] = useState({ name: "", email: "", password: "", role: "admin" });
  const [creating, setCreating] = useState(false);

  const load = () => api.get("/admin/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const setRole = async (id, role) => {
    try { await api.put(`/admin/users/${id}/role`, { role }); toast.success(`Role updated to ${role}`); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Permanently delete ${u.name} (${u.email}) and all their threads/posts?`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("User deleted"); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const createUser = async () => {
    setCreating(true);
    try {
      await api.post("/admin/users", nu);
      toast.success(`Created ${nu.role}: ${nu.email}`);
      setNu({ name: "", email: "", password: "", role: "admin" });
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setCreating(false); }
  };

  const filtered = users.filter(
    (u) => !q || u.email.toLowerCase().includes(q.toLowerCase()) || (u.name || "").toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="space-y-4">
      {/* Create user form */}
      <div className="brutal-border brutal-shadow-sm rounded-xl p-5 bg-white" data-testid="create-user-form">
        <h3 className="font-display font-bold text-lg mb-2 flex items-center gap-2">
          <Plus size={18} /> Create new user
        </h3>
        <p className="text-sm text-zinc-600 font-medium mb-3">
          Create a fresh account with any role — including <b>admin</b>. New admins can also delete other admins.
        </p>
        <div className="grid sm:grid-cols-2 gap-2">
          <Input data-testid="cu-name" placeholder="Name" value={nu.name}
            onChange={(e) => setNu({ ...nu, name: e.target.value })}
            className="brutal-border rounded-xl h-11 focus-visible:ring-0 focus-visible:border-[var(--ink)]" />
          <Input data-testid="cu-email" type="email" placeholder="Email" value={nu.email}
            onChange={(e) => setNu({ ...nu, email: e.target.value })}
            className="brutal-border rounded-xl h-11 focus-visible:ring-0 focus-visible:border-[var(--ink)]" />
          <Input data-testid="cu-password" type="password" placeholder="Password (min 6)" value={nu.password}
            onChange={(e) => setNu({ ...nu, password: e.target.value })}
            className="brutal-border rounded-xl h-11 focus-visible:ring-0 focus-visible:border-[var(--ink)]" />
          <Select value={nu.role} onValueChange={(v) => setNu({ ...nu, role: v })}>
            <SelectTrigger data-testid="cu-role" className="brutal-border rounded-xl h-11 font-bold bg-white focus:ring-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="brutal-border rounded-xl">
              <SelectItem value="user">User</SelectItem>
              <SelectItem value="moderator">Moderator</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button data-testid="cu-create-btn" onClick={createUser}
          disabled={creating || !nu.name || !nu.email || !nu.password}
          className="mt-3 brutal-border brutal-shadow rounded-xl font-bold gap-2"
          style={{ background: "var(--primary)", color: "var(--ink)" }}>
          <Plus size={16} /> {creating ? "Creating..." : `Create ${nu.role}`}
        </Button>
      </div>

      {/* Search */}
      <div className="brutal-border brutal-shadow-sm rounded-xl p-5 bg-white">
        <h3 className="font-display font-bold text-lg mb-2 flex items-center gap-2">
          <Gavel size={18} /> Manage users
        </h3>
        <Input data-testid="mod-search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name or email..."
          className="brutal-border rounded-xl h-11 focus-visible:ring-0 focus-visible:border-[var(--ink)]" />
      </div>

      <div className="space-y-2" data-testid="users-list">
        {filtered.map((u) => {
          const isMe = me?.id === u.id;
          return (
            <div key={u.id} className="brutal-border brutal-shadow-sm rounded-xl p-4 bg-white flex items-center gap-3">
              <div className="w-10 h-10 brutal-border rounded-lg grid place-items-center font-display font-black bg-[var(--accent)]">
                {u.name?.[0]?.toUpperCase() || "?"}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold truncate flex items-center gap-2">
                  {u.name}
                  {isMe && <span className="sticker text-[10px]" style={{ background: "var(--primary)" }}>You</span>}
                </div>
                <div className="text-xs text-zinc-500 font-medium truncate">{u.email}</div>
              </div>
              {isMe ? (
                <span className="sticker" style={{ background: "var(--pink)" }}>{u.role}</span>
              ) : (
                <>
                  <Select value={u.role} onValueChange={(v) => setRole(u.id, v)}>
                    <SelectTrigger data-testid={`role-select-${u.id}`}
                      className="w-[140px] brutal-border rounded-xl h-10 font-bold bg-white focus:ring-0">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="brutal-border rounded-xl">
                      <SelectItem value="user" data-testid={`role-user-${u.id}`}>User</SelectItem>
                      <SelectItem value="moderator" data-testid={`role-mod-${u.id}`}>Moderator</SelectItem>
                      <SelectItem value="admin" data-testid={`role-admin-${u.id}`}>Admin</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={() => removeUser(u)} data-testid={`user-del-${u.id}`}
                    className="h-10 brutal-border brutal-shadow-sm rounded-lg font-bold bg-white hover:bg-white text-[var(--destructive)]">
                    <Trash2 size={14} />
                  </Button>
                </>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && <div className="text-zinc-500 font-medium">No users found.</div>}
      </div>
    </div>
  );
}

function Categories() {
  const [list, setList] = useState([]);
  const [f, setF] = useState({ name: "", description: "", color: "#B5E2FA", icon: "MessageSquare" });
  const load = () => api.get("/categories").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/categories", f);
      setF({ name: "", description: "", color: "#B5E2FA", icon: "MessageSquare" });
      load();
      toast.success("Category created");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete category?")) return;
    await api.delete(`/categories/${id}`); load();
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="brutal-border brutal-shadow-sm rounded-xl p-5 bg-white space-y-3">
        <h3 className="font-display font-bold text-lg">New category</h3>
        <Input data-testid="cat-name" placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })}
          className="brutal-border rounded-xl h-11 focus-visible:ring-0 focus-visible:border-[var(--ink)]" />
        <Textarea data-testid="cat-desc" placeholder="Description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })}
          className="brutal-border rounded-xl focus-visible:ring-0 focus-visible:border-[var(--ink)]" />
        <div className="flex gap-2">
          <Input type="color" value={f.color} onChange={(e) => setF({ ...f, color: e.target.value })}
            className="brutal-border rounded-xl h-11 w-20" data-testid="cat-color" />
          <Input placeholder="Icon (lucide)" value={f.icon} onChange={(e) => setF({ ...f, icon: e.target.value })}
            className="brutal-border rounded-xl h-11 focus-visible:ring-0 focus-visible:border-[var(--ink)] flex-1" data-testid="cat-icon" />
        </div>
        <Button data-testid="cat-create-btn" onClick={create}
          className="brutal-border brutal-shadow rounded-xl font-bold gap-2" style={{ background: "var(--primary)", color: "var(--ink)" }}>
          <Plus size={16} /> Create
        </Button>
      </div>

      <div className="space-y-3">
        {list.map((c) => (
          <div key={c.id} className="brutal-border brutal-shadow-sm rounded-xl p-4 flex items-center gap-3" style={{ background: c.color }}>
            <div className="flex-1">
              <div className="font-display font-bold">{c.name}</div>
              <div className="text-xs text-zinc-700 font-medium">{c.thread_count} threads</div>
            </div>
            <Button onClick={() => del(c.id)} data-testid={`cat-del-${c.id}`}
              className="h-9 brutal-border brutal-shadow-sm rounded-lg font-bold bg-white hover:bg-white text-[var(--destructive)]">
              <Trash2 size={14} />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Banners() {
  const [list, setList] = useState([]);
  const [f, setF] = useState({ title: "", image_url: "", link_url: "", active: true, position: "top" });
  const load = () => api.get("/banners?active_only=false").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/banners", f);
      setF({ title: "", image_url: "", link_url: "", active: true, position: "top" });
      load();
      toast.success("Banner created");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const toggle = async (b) => { await api.put(`/banners/${b.id}`, { ...b, active: !b.active }); load(); };
  const del = async (id) => { if (!window.confirm("Delete banner?")) return; await api.delete(`/banners/${id}`); load(); };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="brutal-border brutal-shadow-sm rounded-xl p-5 bg-white space-y-3">
        <h3 className="font-display font-bold text-lg">New ad banner</h3>
        <Input data-testid="ban-title" placeholder="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })}
          className="brutal-border rounded-xl h-11 focus-visible:ring-0 focus-visible:border-[var(--ink)]" />
        <Input data-testid="ban-image" placeholder="Image URL" value={f.image_url} onChange={(e) => setF({ ...f, image_url: e.target.value })}
          className="brutal-border rounded-xl h-11 focus-visible:ring-0 focus-visible:border-[var(--ink)]" />
        <Input data-testid="ban-link" placeholder="Link URL" value={f.link_url} onChange={(e) => setF({ ...f, link_url: e.target.value })}
          className="brutal-border rounded-xl h-11 focus-visible:ring-0 focus-visible:border-[var(--ink)]" />
        <div className="flex items-center gap-2">
          <Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="ban-active" />
          <span className="text-sm font-bold">Active</span>
        </div>
        <Button data-testid="ban-create-btn" onClick={create}
          className="brutal-border brutal-shadow rounded-xl font-bold gap-2" style={{ background: "var(--accent)", color: "var(--ink)" }}>
          <Plus size={16} /> Add
        </Button>
      </div>

      <div className="space-y-3">
        {list.length === 0 && <div className="text-zinc-500 font-medium">No banners yet.</div>}
        {list.map((b) => (
          <div key={b.id} className="brutal-border brutal-shadow-sm rounded-xl p-4 bg-white flex gap-3">
            {b.image_url && <img src={b.image_url} alt="" className="w-16 h-16 brutal-border rounded-lg object-cover" onError={(e) => { e.target.style.display = "none"; }} />}
            <div className="flex-1">
              <div className="font-bold">{b.title}</div>
              <div className="text-xs text-zinc-500 font-medium truncate">{b.link_url}</div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={b.active} onCheckedChange={() => toggle(b)} data-testid={`ban-toggle-${b.id}`} />
              <Button onClick={() => del(b.id)} data-testid={`ban-del-${b.id}`}
                className="h-9 brutal-border brutal-shadow-sm rounded-lg font-bold bg-white hover:bg-white text-[var(--destructive)]">
                <Trash2 size={14} />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Memberships() {
  const [list, setList] = useState([]);
  const [f, setF] = useState({ name: "", description: "", price: 9.99, currency: "usd", duration_days: 30, features: "", active: true });
  const load = () => api.get("/memberships?active_only=false").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/memberships", {
        ...f,
        price: parseFloat(f.price),
        duration_days: parseInt(f.duration_days),
        features: f.features.split("\n").filter(Boolean),
      });
      setF({ name: "", description: "", price: 9.99, currency: "usd", duration_days: 30, features: "", active: true });
      load();
      toast.success("Package created");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/memberships/${id}`); load(); };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="brutal-border brutal-shadow-sm rounded-xl p-5 bg-white space-y-3">
        <h3 className="font-display font-bold text-lg">New membership package</h3>
        <Input data-testid="mem-name" placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })}
          className="brutal-border rounded-xl h-11 focus-visible:ring-0 focus-visible:border-[var(--ink)]" />
        <Textarea data-testid="mem-desc" placeholder="Description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })}
          className="brutal-border rounded-xl focus-visible:ring-0 focus-visible:border-[var(--ink)]" />
        <div className="grid grid-cols-2 gap-2">
          <Input data-testid="mem-price" type="number" step="0.01" placeholder="Price" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })}
            className="brutal-border rounded-xl h-11 focus-visible:ring-0 focus-visible:border-[var(--ink)]" />
          <Input data-testid="mem-days" type="number" placeholder="Days" value={f.duration_days} onChange={(e) => setF({ ...f, duration_days: e.target.value })}
            className="brutal-border rounded-xl h-11 focus-visible:ring-0 focus-visible:border-[var(--ink)]" />
        </div>
        <Textarea data-testid="mem-features" placeholder="Features (one per line)" value={f.features} onChange={(e) => setF({ ...f, features: e.target.value })}
          className="brutal-border rounded-xl focus-visible:ring-0 focus-visible:border-[var(--ink)]" rows={3} />
        <Button data-testid="mem-create-btn" onClick={create}
          className="brutal-border brutal-shadow rounded-xl font-bold gap-2" style={{ background: "var(--pink)", color: "var(--ink)" }}>
          <Plus size={16} /> Create
        </Button>
      </div>

      <div className="space-y-3">
        {list.map((p) => (
          <div key={p.id} className="brutal-border brutal-shadow-sm rounded-xl p-4 bg-white flex items-start gap-3">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <Crown size={16} />
                <div className="font-display font-bold">{p.name}</div>
                <span className="sticker text-[10px]" style={{ background: "var(--accent)" }}>${p.price} / {p.duration_days}d</span>
              </div>
              <div className="text-xs text-zinc-700 font-medium mt-1">{p.description}</div>
            </div>
            <Button onClick={() => del(p.id)} data-testid={`mem-del-${p.id}`}
              className="h-9 brutal-border brutal-shadow-sm rounded-lg font-bold bg-white hover:bg-white text-[var(--destructive)]">
              <Trash2 size={14} />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Payments() {
  const [s, setS] = useState(null);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({ stripe_api_key: "", paypal_client_id: "", paypal_client_secret: "", paypal_mode: "sandbox" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/settings/payments")
    .then((r) => { setS(r.data); setForm((f) => ({ ...f, paypal_mode: r.data.paypal_mode || "sandbox" })); setErr(""); })
    .catch((e) => setErr(formatError(e.response?.data?.detail) || e.message || "Failed to load"));
  useEffect(() => { load(); }, []);

  const saveStripe = async () => {
    setSaving(true);
    try { await api.put("/admin/settings/payments", { stripe_api_key: form.stripe_api_key }); toast.success("Stripe key saved"); setForm({ ...form, stripe_api_key: "" }); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const savePaypal = async () => {
    setSaving(true);
    try {
      const payload = { paypal_mode: form.paypal_mode };
      if (form.paypal_client_id) payload.paypal_client_id = form.paypal_client_id;
      if (form.paypal_client_secret) payload.paypal_client_secret = form.paypal_client_secret;
      await api.put("/admin/settings/payments", payload);
      toast.success("PayPal settings saved");
      setForm({ ...form, paypal_client_id: "", paypal_client_secret: "" });
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const clearStripe = async () => { await api.put("/admin/settings/payments", { stripe_api_key: "" }); toast.success("Stripe key removed"); load(); };
  const clearPaypal = async () => { await api.put("/admin/settings/payments", { paypal_client_id: "", paypal_client_secret: "" }); toast.success("PayPal credentials removed"); load(); };

  if (err) return <div data-testid="payments-error" className="brutal-border brutal-shadow-sm rounded-xl p-4 bg-red-50 text-[var(--destructive)] font-bold">{err}</div>;
  if (!s) return <div data-testid="payments-loading">Loading...</div>;

  return (
    <div className="max-w-3xl space-y-6">
      {/* Stripe */}
      <div className="brutal-border brutal-shadow-sm rounded-xl p-6 bg-white" data-testid="stripe-section">
        <h3 className="font-display font-bold text-lg flex items-center gap-2"><CreditCard size={18} /> Stripe</h3>
        <p className="text-sm text-zinc-600 font-medium mt-2">
          Get your secret key at <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noreferrer" className="font-bold underline">dashboard.stripe.com/apikeys</a>.
        </p>
        <div className="mt-4 brutal-border rounded-lg p-3 flex items-center gap-2 font-bold text-sm"
          style={{ background: s.stripe_api_key_set ? "var(--green)" : "var(--accent)" }}>
          {s.stripe_api_key_set
            ? <><Check size={14} strokeWidth={2.5} /> Active: <code className="bg-white px-2 py-0.5 rounded">{s.stripe_api_key_masked}</code></>
            : <><AlertCircle size={14} strokeWidth={2.5} /> Not configured</>}
        </div>
        <div className="mt-4 space-y-2">
          <Input data-testid="stripe-key-input" type="password" value={form.stripe_api_key}
            onChange={(e) => setForm({ ...form, stripe_api_key: e.target.value })} placeholder="sk_live_... or sk_test_..."
            className="brutal-border rounded-xl h-11 focus-visible:ring-0 focus-visible:border-[var(--ink)] font-mono" />
          <div className="flex gap-2">
            <Button data-testid="stripe-save-btn" onClick={saveStripe} disabled={!form.stripe_api_key || saving}
              className="brutal-border brutal-shadow rounded-xl font-bold gap-2" style={{ background: "var(--primary)", color: "var(--ink)" }}>
              <Save size={16} /> Save Stripe
            </Button>
            {s.stripe_api_key_set && (
              <Button data-testid="stripe-clear-btn" onClick={clearStripe}
                className="brutal-border brutal-shadow-sm rounded-xl font-bold bg-white hover:bg-white text-[var(--destructive)]">Remove</Button>
            )}
          </div>
        </div>
      </div>

      {/* PayPal */}
      <div className="brutal-border brutal-shadow-sm rounded-xl p-6 bg-white" data-testid="paypal-section">
        <h3 className="font-display font-bold text-lg flex items-center gap-2"><CreditCard size={18} /> PayPal</h3>
        <p className="text-sm text-zinc-600 font-medium mt-2">
          Create a REST app at <a href="https://developer.paypal.com/dashboard/applications" target="_blank" rel="noreferrer" className="font-bold underline">developer.paypal.com</a> and copy Client ID + Secret.
        </p>
        <div className="mt-4 brutal-border rounded-lg p-3 flex items-center gap-2 font-bold text-sm"
          style={{ background: (s.paypal_client_id_set && s.paypal_client_secret_set) ? "var(--green)" : "var(--accent)" }}>
          {(s.paypal_client_id_set && s.paypal_client_secret_set)
            ? <><Check size={14} strokeWidth={2.5} /> Active ({s.paypal_mode}): <code className="bg-white px-2 py-0.5 rounded">{s.paypal_client_id_masked}</code></>
            : <><AlertCircle size={14} strokeWidth={2.5} /> Not configured</>}
        </div>
        <div className="mt-4 space-y-2">
          <Input data-testid="pp-id-input" value={form.paypal_client_id}
            onChange={(e) => setForm({ ...form, paypal_client_id: e.target.value })} placeholder="PayPal Client ID"
            className="brutal-border rounded-xl h-11 focus-visible:ring-0 focus-visible:border-[var(--ink)] font-mono" />
          <Input data-testid="pp-secret-input" type="password" value={form.paypal_client_secret}
            onChange={(e) => setForm({ ...form, paypal_client_secret: e.target.value })} placeholder="PayPal Client Secret"
            className="brutal-border rounded-xl h-11 focus-visible:ring-0 focus-visible:border-[var(--ink)] font-mono" />
          <div className="flex items-center gap-3">
            <label className="text-xs uppercase font-bold tracking-widest">Mode</label>
            <Select value={form.paypal_mode} onValueChange={(v) => setForm({ ...form, paypal_mode: v })}>
              <SelectTrigger data-testid="pp-mode" className="w-[160px] brutal-border rounded-xl h-10 font-bold bg-white focus:ring-0"><SelectValue /></SelectTrigger>
              <SelectContent className="brutal-border rounded-xl">
                <SelectItem value="sandbox">Sandbox (test)</SelectItem>
                <SelectItem value="live">Live (production)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            <Button data-testid="pp-save-btn" onClick={savePaypal} disabled={saving}
              className="brutal-border brutal-shadow rounded-xl font-bold gap-2" style={{ background: "var(--primary)", color: "var(--ink)" }}>
              <Save size={16} /> Save PayPal
            </Button>
            {(s.paypal_client_id_set || s.paypal_client_secret_set) && (
              <Button data-testid="pp-clear-btn" onClick={clearPaypal}
                className="brutal-border brutal-shadow-sm rounded-xl font-bold bg-white hover:bg-white text-[var(--destructive)]">Remove</Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Homepage() {
  const [h, setH] = useState(null);
  useEffect(() => { api.get("/homepage").then((r) => setH(r.data)); }, []);

  const save = async () => {
    try {
      await api.put("/homepage", h);
      toast.success("Homepage updated");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  if (!h) return <div data-testid="home-editor-loading">Loading...</div>;

  return (
    <div className="max-w-2xl brutal-border brutal-shadow-sm rounded-xl p-6 bg-white space-y-4">
      <h3 className="font-display font-bold text-lg">Edit homepage</h3>
      <div>
        <label className="text-xs uppercase font-bold tracking-widest">Hero title</label>
        <Input data-testid="home-hero-title" value={h.hero_title} onChange={(e) => setH({ ...h, hero_title: e.target.value })}
          className="mt-1 brutal-border rounded-xl h-11 focus-visible:ring-0 focus-visible:border-[var(--ink)]" />
      </div>
      <div>
        <label className="text-xs uppercase font-bold tracking-widest">Subtitle</label>
        <Textarea data-testid="home-hero-subtitle" value={h.hero_subtitle} onChange={(e) => setH({ ...h, hero_subtitle: e.target.value })}
          className="mt-1 brutal-border rounded-xl focus-visible:ring-0 focus-visible:border-[var(--ink)]" />
      </div>
      <div>
        <label className="text-xs uppercase font-bold tracking-widest">CTA text</label>
        <Input data-testid="home-hero-cta" value={h.hero_cta} onChange={(e) => setH({ ...h, hero_cta: e.target.value })}
          className="mt-1 brutal-border rounded-xl h-11 focus-visible:ring-0 focus-visible:border-[var(--ink)]" />
      </div>
      <div>
        <label className="text-xs uppercase font-bold tracking-widest">Announcement (top banner)</label>
        <Input data-testid="home-announcement" value={h.announcement || ""} onChange={(e) => setH({ ...h, announcement: e.target.value })}
          className="mt-1 brutal-border rounded-xl h-11 focus-visible:ring-0 focus-visible:border-[var(--ink)]" />
      </div>
      <Button data-testid="home-save-btn" onClick={save}
        className="brutal-border brutal-shadow rounded-xl font-bold gap-2" style={{ background: "var(--primary)", color: "var(--ink)" }}>
        <Save size={16} /> Save changes
      </Button>
    </div>
  );
}
